// src/Dashboard/admin/sections/ReportsSection.jsx
import { Download, RefreshCw, FileText, Building2, CheckCircle2 } from "lucide-react";

function safeNumber(x, fallback = 0) {
  const n = Number(x);
  return Number.isFinite(n) ? n : fallback;
}

export default function ReportsSection({
  // loading + data
  reportsLoading,
  reportsError,
  reportsSummary,

  // derived totals (optional)
  totals,

  // filters (controlled)
  repStatus,
  setRepStatus,
  repGroupBy,
  setRepGroupBy,
  repQ,
  setRepQ,
  repCourseSlug,
  setRepCourseSlug,
  repIncludeCourses,
  setRepIncludeCourses,

  // scope display
  scopeChips = [],

  // actions
  onRefresh,
  onExportCsv,
  onExportPdf,
}) {
  const computedTotals =
    totals ||
    reportsSummary?.totals || {
      total: 0,
      active: 0,
      inactive: 0,
      graduated: 0,
      notGraduated: 0,
      avgProgress: 0,
    };

  const courses = Array.isArray(reportsSummary?.courses) ? reportsSummary.courses : [];
  const grouped = Array.isArray(reportsSummary?.grouped) ? reportsSummary.grouped : [];

  return (
    <div className="space-y-6">
      {/* Header + Filters */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 p-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold">Reports & Analytics</h2>
            <p className="text-xs text-slate-500">
              Server-backed summary + exports (scope enforced). Filters apply to summary + exports.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onRefresh}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
            >
              <RefreshCw size={16} className={reportsLoading ? "animate-spin" : ""} />
              Refresh
            </button>

            <button
              type="button"
              onClick={async () => {
                try {
                  await onExportCsv?.();
                } catch (e) {
                  alert(e?.message || "CSV export failed");
                }
              }}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-900 text-white dark:bg-slate-200 dark:text-slate-900 text-sm font-semibold"
            >
              <Download size={16} />
              CSV
            </button>

            <button
              type="button"
              onClick={async () => {
                try {
                  await onExportPdf?.();
                } catch (e) {
                  alert(e?.message || "PDF export failed");
                }
              }}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold"
            >
              <FileText size={16} />
              PDF
            </button>
          </div>
        </div>

        {reportsError ? (
          <div className="mt-4 p-3 rounded-xl border border-amber-200 bg-amber-50 text-amber-800 text-sm dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-100">
            {reportsError}
          </div>
        ) : null}

        <div className="mt-5 grid md:grid-cols-5 gap-4">
          <SelectField
            label="Status"
            value={repStatus}
            onChange={setRepStatus}
            options={[
              { value: "active", label: "Active" },
              { value: "inactive", label: "Inactive" },
              { value: "all", label: "All" },
            ]}
          />

          <SelectField
            label="Group by"
            value={repGroupBy}
            onChange={setRepGroupBy}
            options={[
              { value: "none", label: "None" },
              { value: "conference", label: "Conference" },
              { value: "district", label: "District" },
              { value: "church", label: "Church" },
            ]}
          />

          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">
              Search (name/email)
            </label>
            <input
              value={repQ}
              onChange={(e) => setRepQ(e.target.value)}
              placeholder="e.g. Okello, learner@email.com"
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">
              Course slug (optional)
            </label>
            <input
              value={repCourseSlug}
              onChange={(e) => setRepCourseSlug(e.target.value)}
              placeholder="discover"
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm"
            />
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between gap-3">
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={!!repIncludeCourses}
              onChange={(e) => setRepIncludeCourses(e.target.checked)}
              className="w-4 h-4"
            />
            Include per-course metrics
          </label>

          <div className="text-xs text-slate-500 flex items-center gap-2">
            <Building2 size={14} />
            <span>
              Scope:{" "}
              <span className="font-semibold">
                {(scopeChips || []).length ? scopeChips.join(" • ") : "Union (All)"}
              </span>
            </span>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Totals */}
        <div className="lg:col-span-1 bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 p-6">
          <h3 className="text-lg font-semibold mb-4">Totals</h3>
          <div className="space-y-3">
            <RowStat label="Total learners" value={computedTotals.total} />
            <RowStat label="Active" value={computedTotals.active} />
            <RowStat label="Inactive" value={computedTotals.inactive} />
            <RowStat label="Graduated" value={computedTotals.graduated} />
            <RowStat label="Avg progress" value={`${safeNumber(computedTotals.avgProgress)}%`} />
          </div>
        </div>

        {/* Courses summary + grouped */}
        <div className="lg:col-span-2 bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Courses Summary</h3>
            <div className="text-xs text-slate-500">{reportsLoading ? "Loading…" : ""}</div>
          </div>

          <div className="mt-4 overflow-auto rounded-xl border border-slate-200 dark:border-slate-800">
            <table className="w-full text-xs sm:text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/70 text-slate-700 dark:text-slate-200">
                <tr>
                  <th className="p-3 text-left">Course</th>
                  <th className="p-3 text-center">Enrolled</th>
                  <th className="p-3 text-center">Completed</th>
                  <th className="p-3 text-center">Graduated</th>
                  <th className="p-3 text-center">Pass Rate</th>
                </tr>
              </thead>
              <tbody>
                {courses.map((c) => (
                  <tr
                    key={c.course_slug || c.slug}
                    className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                  >
                    <td className="p-3 font-semibold">{c.course_slug || c.slug || "—"}</td>
                    <td className="p-3 text-center">{c.enrolled ?? 0}</td>
                    <td className="p-3 text-center">{c.learners_completed ?? 0}</td>
                    <td className="p-3 text-center">{c.learners_graduated ?? 0}</td>
                    <td className="p-3 text-center">{Math.round(c.pass_rate_pct ?? 0)}%</td>
                  </tr>
                ))}

                {courses.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-10 text-center text-slate-500">
                      No course results for this scope/filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {repGroupBy !== "none" && (
            <div className="mt-6">
              <h4 className="text-sm font-semibold mb-2">Grouped Summary ({repGroupBy})</h4>
              <div className="overflow-auto rounded-xl border border-slate-200 dark:border-slate-800">
                <table className="w-full text-xs sm:text-sm">
                  <thead className="bg-slate-50 dark:bg-slate-800/70 text-slate-700 dark:text-slate-200">
                    <tr>
                      <th className="p-3 text-left">Group</th>
                      <th className="p-3 text-center">Learners</th>
                      <th className="p-3 text-center">Graduated</th>
                      <th className="p-3 text-center">Avg progress</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grouped.map((g, idx) => (
                      <tr
                        key={`${g.name || "group"}-${idx}`}
                        className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                      >
                        <td className="p-3">
                          <div className="font-semibold truncate">{g.name || "—"}</div>
                        </td>
                        <td className="p-3 text-center">{g.total ?? 0}</td>
                        <td className="p-3 text-center">{g.graduated ?? 0}</td>
                        <td className="p-3 text-center">{Math.round(g.avgProgress ?? 0)}%</td>
                      </tr>
                    ))}

                    {grouped.length === 0 && (
                      <tr>
                        <td colSpan={4} className="py-8 text-center text-slate-500">
                          No groups for this scope/filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Tiny “OK” hint when loaded */}
          {!reportsLoading && !reportsError ? (
            <div className="mt-3 text-xs text-slate-500 inline-flex items-center gap-2">
              <CheckCircle2 size={14} className="text-green-600" />
              Summary loaded
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Local small UI components                                                   */
/* -------------------------------------------------------------------------- */

function SelectField({ label, value, onChange, options, disabled }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">{label}</label>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="w-full appearance-none px-3 pr-9 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm disabled:opacity-60"
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">▾</span>
      </div>
    </div>
  );
}

function RowStat({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="text-slate-600 dark:text-slate-300">{label}</span>
      <span className="font-semibold">{value}</span>
    </div>
  );
}
